import type { FC } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Alert, Box, Button, TextField, Typography } from '@mui/material'
import { AccessTime, Label, Layers, WarningAmber } from '@mui/icons-material'
import { CopyButton } from '@/common/components'
import type { EnvironmentValue } from '../../constants'
import { ENVIRONMENT_OPTIONS } from '../../constants'
import type { ApiKeyResponse } from '../../types'

interface ApiKeyDisplayStepProps {
  readonly apiKey: ApiKeyResponse
  readonly onClose: () => void
}

export const ApiKeyDisplayStep: FC<ApiKeyDisplayStepProps> = ({ apiKey, onClose }) => {
  const environment = useMemo(() => {
    if (apiKey.type) return apiKey.type
    // Extract environment from key format: c4-{env}-{rest}
    const envRegex = /^c4-(\w+)-/
    const match = envRegex.exec(apiKey.apiKey)
    return match?.[1] as EnvironmentValue | undefined
  }, [apiKey.type, apiKey.apiKey])

  const environmentLabel = useMemo(() => {
    if (!environment) return 'Unknown'
    return ENVIRONMENT_OPTIONS.find((opt) => opt.value === environment)?.label ?? environment
  }, [environment])
  return (
    <Box sx={containerSx}>
      <Typography variant="h6" sx={titleSx}>
        API Key Generated
      </Typography>

      <Alert severity="warning" icon={<WarningAmber />} sx={warningSx}>
        Copy your API key. You will not be able to see it again
      </Alert>

      <Box sx={keyContainerSx}>
        <TextField
          value={apiKey.apiKey}
          fullWidth
          InputProps={{
            readOnly: true,
            sx: keyInputSx,
          }}
        />
        <CopyButton text={apiKey.apiKey} tooltipText="Copy API key" />
      </Box>

      <Box sx={infoSx}>
        <Box sx={infoRowSx}>
          <Label sx={infoIconSx} />
          <Typography variant="body1" color="text.secondary" sx={infoLabelSx}>
            Name
          </Typography>
          <Typography variant="body1" color="text.primary" sx={infoValueSx}>
            {apiKey.name}
          </Typography>
        </Box>
        <Box sx={infoRowSx}>
          <Layers sx={infoIconSx} />
          <Typography variant="body1" color="text.secondary" sx={infoLabelSx}>
            Environment
          </Typography>
          <Typography variant="body1" color="text.primary" sx={infoValueSx}>
            {environmentLabel}
          </Typography>
        </Box>
        <Box sx={infoRowSx}>
          <AccessTime sx={apiKey.expiresAt ? infoIconSx : infoIconWarningSx} />
          <Typography variant="body1" color="text.secondary" sx={infoLabelSx}>
            Expires
          </Typography>
          <Typography
            variant="body1"
            color={apiKey.expiresAt ? 'text.primary' : 'warning.main'}
            sx={infoValueSx}
          >
            {apiKey.expiresAt ? new Date(apiKey.expiresAt).toLocaleDateString() : 'Never'}
          </Typography>
        </Box>
      </Box>

      <Box sx={securityTipsSx}>
        <Typography variant="subtitle2" sx={tipsHeaderSx}>
          Security Tips
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Store this key securely - treat it like a password
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Never commit API keys to version control
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Rotate keys periodically for better security
        </Typography>
      </Box>

      <Box sx={buttonContainerSx}>
        <Button variant="contained" onClick={onClose} sx={buttonSx}>
          Done
        </Button>
      </Box>
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  py: 2,
}

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  mb: 2,
}

const warningSx: SxProps<Theme> = {
  mb: 3,
  alignItems: 'center',
}

const keyContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 3,
}

const keyInputSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
}

const infoSx: SxProps<Theme> = {
  p: 2,
  borderRadius: 1,
  bgcolor: 'action.hover',
  mb: 3,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}

const infoRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}

const infoIconSx: SxProps<Theme> = {
  fontSize: 20,
  color: 'primary.main',
}

const infoIconWarningSx: SxProps<Theme> = {
  fontSize: 20,
  color: 'warning.main',
}

const infoLabelSx: SxProps<Theme> = {
  fontWeight: 500,
  minWidth: 70,
}

const infoValueSx: SxProps<Theme> = {
  fontWeight: 400,
}

const securityTipsSx: SxProps<Theme> = {
  p: 2,
  borderRadius: 1,
  border: 1,
  borderColor: 'divider',
  mb: 3,
}

const tipsHeaderSx: SxProps<Theme> = {
  mb: 1,
}

const buttonContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  mt: 4,
}

const buttonSx: SxProps<Theme> = {
  minWidth: 120,
}
